/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CloseIcon from '@material-ui/icons/Close';
import { ClickAwayListener } from './ClickAwayListener';
import { MenuOption } from './type';

type ModalMenuProps = {
  menuOptions?: MenuOption[];
  isMenuOpen: boolean;
  setIsMenuOpen: (isOpen: boolean) => void;
  modalTitle: string;
  modalSubtitle?: string;
  size?: 'medium' | 'large';
  description?: string | JSX.Element;
};

export const ModalMenu = ({
  menuOptions = [],
  isMenuOpen,
  setIsMenuOpen,
  modalTitle = '',
  modalSubtitle,
  size = 'medium',
  description = '',
}: ModalMenuProps): JSX.Element => {
  const reorderedOptions = menuOptions;
  return (
    <ClickAwayListener
      onClickAway={() => {
        setIsMenuOpen(false);
      }}
      isActive={isMenuOpen}
    >
      <div css={styles.menuContainer}>
        {isMenuOpen && (
          <>
            <div
              css={styles.overlay}
              onClick={() => {
                setIsMenuOpen(false);
              }}
            />
            <div css={styles.modalContainer(size)}>
              <div css={styles.headerContainer}>
                <div css={styles.modalHeader}>
                  <h3 css={styles.modalTitle}>{modalTitle}</h3>
                  <button
                    css={styles.closeButton}
                    onClick={() => {
                      setIsMenuOpen(false);
                    }}
                    aria-label="閉じる"
                    type="button"
                  >
                    <CloseIcon />
                  </button>
                </div>
                {modalSubtitle && (
                  <div css={styles.modalSubtitle}>{modalSubtitle}</div>
                )}
              </div>
              <div css={styles.divider} />
              {menuOptions.length > 0 ? (
                <div css={styles.menuListItems}>
                  {reorderedOptions.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        option.onClick?.();
                        setIsMenuOpen(false);
                      }}
                      css={styles.menuListIcon}
                      type="button"
                    >
                      <div css={styles.listItemLabel}>
                        {option.startIcon && (
                          <span
                            css={styles.buttonIcon}
                            style={{ color: option.color || '#666' }}
                          >
                            {option.startIcon}
                          </span>
                        )}
                        <div css={styles.buttonText}>
                          <div
                            css={styles.buttonLabel}
                            style={{ color: option.color || '#333' }}
                          >
                            {option.label}
                          </div>
                        </div>
                      </div>
                      <ChevronRightIcon
                        style={{ width: 24, height: 24, color: '#ccc' }}
                      />
                    </button>
                  ))}
                </div>
              ) : (
                <div css={styles.descriptionContent}>
                  <div css={styles.descriptionText}>{description}</div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </ClickAwayListener>
  );
};

const styles = {
  menuContainer: css`
    position: relative;
  `,
  overlay: css`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1000;
  `,
  modalContainer: (size: 'medium' | 'large') => css`
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: #ffffff;
    border-radius: 16px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
    z-index: 1001;
    width: ${size === 'large' ? '360px' : '260px'};
    max-width: 90vw;
  `,
  headerContainer: css`
    padding: 16px 24px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  `,
  modalHeader: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
  `,
  modalTitle: css`
    font-size: 18px;
    font-weight: 700;
    color: #333;
    margin: 0;
  `,
  modalSubtitle: css`
    font-size: 14px;
    color: #666;
  `,
  divider: css`
    border-bottom: 1px solid #e0e0e0;
  `,
  menuListItems: css`
    padding: 8px 0;
  `,
  closeButton: css`
    background: none;
    border: none;
    cursor: pointer;
    color: #999;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: background-color 0.2s, color 0.2s;

    &:hover {
      background-color: #f0f0f0;
      color: #666;
    }

    svg {
      font-size: 24px;
    }
  `,
  menuListIcon: css`
    width: 100%;
    background: none;
    border: none;
    padding: 16px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    transition: background-color 0.15s;

    &:hover {
      background-color: #f8f8f8;
    }

    &:active {
      background-color: #f0f0f0;
    }
  `,
  listItemLabel: css`
    display: flex;
    align-items: center;
    gap: 8px;
  `,
  buttonIcon: css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #666;

    svg {
      font-size: 24px;
    }
  `,
  buttonText: css`
    text-align: left;
  `,
  buttonLabel: css`
    font-size: 16px;
    font-weight: 500;
    color: #333;
  `,
  descriptionContent: css`
    padding: 24px;
  `,
  descriptionText: css`
    font-size: 14px;
    line-height: 1.6;
    color: #666;
    margin: 0;
    white-space: pre-wrap;
  `,
};
